import { Box, Input, Skeleton, Text } from "@mantine/core";
import {
  BellIcon,
  CalendarPlusIcon,
  CurrencyCircleDollarIcon,
} from "@phosphor-icons/react";
import { useDisclosure } from "@mantine/hooks";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useSwr from "swr";
import { NetworkSettlementApi } from "../../apis";
import { ExtendPaymentModal } from "../invoice-actions/extend-payment-modal";
import { LateFeeDrawer } from "../invoice-actions/late-fee-drawer";
import { ReminderDrawer } from "../invoice-actions/reminder-drawer";
import { DocNoCell, ListFooter, ListStates, RowActions } from "../list";
import { usePermission } from "../permission";
import { filterInputStyles } from "../ui/shared-styles";
import { useLateFeeConfig } from "../../hooks/use-late-fee-config";
import { currencyFormat } from "../../utils";
import { dateFormat } from "../../utils/date";
import type { AgingBucketKey } from "./aging-chart";
import classes from "../../pages/customer-billing/BillingDetails.module.css";
import listSticky from "../list/list.module.css";
import { dueDay, daysUntilDue } from "../../utils/due-day";

export type InvoiceSeg = "OPEN" | "OVERDUE" | "PENDING" | "CLOSED";

type Props = {
  businessId: string;
  seg: InvoiceSeg;
  bucket: AgingBucketKey | null;
  page: number;
  q: string;
  onSegChange: (seg: InvoiceSeg) => void;
  onBucketClear: () => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (limit: number) => void;
  onSearchChange: (q: string) => void;
  pageSize: number;
  onOldestLoaded?: (oldest: { refCode: string; days: number; amount: number } | null) => void;
  onEarliestOpenDate?: (date: string | null) => void;
  /** Expose first overdue id for page-level reminder. */
  onFirstOverdueId?: (id: string | null) => void;
};

const BUCKET_OVERDUE: Record<AgingBucketKey, string[]> = {
  NOT_DUE: ["NORMAL"],
  D1_30: ["ONE_THREE", "FOUR_THIRTY"],
  D31_60: ["THIRTY_ONE_SIXTY"],
  D61_90: ["SIXTY_ONE_NINETY"],
  D90_PLUS: ["OVER_NINETY"],
};

function isUnsupportedFilterError(err: unknown): boolean {
  const msg = String((err as { message?: string })?.message ?? err ?? "").toLowerCase();
  return (
    msg.includes("agingbucket") ||
    msg.includes("aging_bucket") ||
    msg.includes("must be one of") ||
    msg.includes("validation") ||
    msg.includes("400") ||
    msg.includes("type")
  );
}

function calendarDaysUntil(dueDate: string | Date): number {
  return daysUntilDue(typeof dueDate === "string" ? dueDate : dueDate.toISOString()) ?? 0;
}

function apiTypeForSeg(seg: InvoiceSeg): string {
  if (seg === "PENDING") return "PENDING";
  if (seg === "CLOSED") return "CLOSED";
  if (seg === "OVERDUE") return "OVERDUE";
  return "OPEN";
}

function clientMatchBucket(row: any, bucket: AgingBucketKey | null): boolean {
  if (!bucket) return true;
  const codes = BUCKET_OVERDUE[bucket];
  return codes.includes(row?.overdueStatus);
}

function DuePill({ paymentDate }: { paymentDate?: string | null }) {
  const { t } = useTranslation("common");
  if (!paymentDate) return <Text className={classes.amtMuted}>-</Text>;
  const days = calendarDaysUntil(paymentDate);
  if (days < 0) {
    return (
      <Text component="span" className={`${classes.duePill} ${classes.duePillOverdue}`}>
        {t("list.dueOverdue", { days: Math.abs(days) })}
      </Text>
    );
  }
  if (days === 0) {
    return (
      <Text component="span" className={`${classes.duePill} ${classes.duePillToday}`}>
        {t("list.dueToday")}
      </Text>
    );
  }
  return (
    <Text component="span" className={`${classes.duePill} ${classes.duePillOk}`}>
      {t("list.dueRemaining", { days })}
    </Text>
  );
}

export function CustomerInvoiceTable({
  businessId,
  seg,
  bucket,
  page,
  q,
  onSegChange,
  onBucketClear,
  onPageChange,
  onPageSizeChange,
  onSearchChange,
  pageSize,
  onOldestLoaded,
  onEarliestOpenDate,
  onFirstOverdueId,
}: Props) {
  const { t } = useTranslation(["billing", "common", "invoice"]);
  const navigate = useNavigate();
  const INV_OPEN = usePermission("INV_NETWORK_SETTLEMENT_GET_OPEN");

  const [searchInput, setSearchInput] = useState(q);
  const [useAgingParam, setUseAgingParam] = useState(true);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState("");
  const [selectedInvoiceNumber, setSelectedInvoiceNumber] = useState("");
  const [selectedDueDate, setSelectedDueDate] = useState("");
  const [extendOpened, extendHandlers] = useDisclosure(false);
  const [lateFeeOpened, lateFeeHandlers] = useDisclosure(false);
  const [reminderOpened, reminderHandlers] = useDisclosure(false);
  const { lateFeeEnabled } = useLateFeeConfig();

  useEffect(() => {
    setSearchInput(q);
  }, [q]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (searchInput === q) return;
      onSearchChange(searchInput);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [searchInput, q, onSearchChange]);

  const fetchKey = `cust.inv.${businessId}.${seg}.${bucket ?? ""}.${page}.${pageSize}.${q}.aging=${useAgingParam}`;

  const { data, error, isLoading, mutate } = useSwr(
    businessId && INV_OPEN.hasAccess ? fetchKey : null,
    async () => {
      const type = apiTypeForSeg(seg);
      const filter: Record<string, string> = { businessId, type };
      if (bucket && useAgingParam && (seg === "OPEN" || seg === "OVERDUE")) {
        filter.agingBucket = bucket;
      }

      try {
        const res = await NetworkSettlementApi.invoice({
          offset: { page, limit: pageSize },
          filter,
        });
        let rows = (res?.rows as any[]) ?? [];
        let count = res?.count ?? rows.length;

        // Client fallback when DEV has no agingBucket yet
        if (bucket && !useAgingParam) {
          const all = await NetworkSettlementApi.invoice({
            offset: { page: 1, limit: 500 },
            filter: { businessId, type },
          });
          rows = ((all?.rows as any[]) ?? []).filter((r) => clientMatchBucket(r, bucket));
          if (q.trim()) {
            const lowered = q.trim().toLowerCase();
            rows = rows.filter((r) => String(r.refCode ?? "").toLowerCase().includes(lowered));
          }
          count = rows.length;
          const start = (page - 1) * pageSize;
          rows = rows.slice(start, start + pageSize);
        } else if (q.trim()) {
          // Server may not support q — soft client filter on page when searching
          const lowered = q.trim().toLowerCase();
          const filtered = rows.filter((r) => String(r.refCode ?? "").toLowerCase().includes(lowered));
          if (filtered.length !== rows.length) {
            rows = filtered;
          }
        }

        return { count, rows };
      } catch (err) {
        if (bucket && useAgingParam && isUnsupportedFilterError(err)) {
          setUseAgingParam(false);
          throw err;
        }
        // Fallback OPEN/OVERDUE types
        if ((seg === "OPEN" || seg === "OVERDUE") && isUnsupportedFilterError(err)) {
          const confirmed = await NetworkSettlementApi.invoice({
            offset: { page: 1, limit: 500 },
            filter: { businessId, type: "CONFIRMED" },
          });
          let rows = ((confirmed?.rows as any[]) ?? []).filter((r) => {
            if (r.paymentStatus === "CLOSED") return false;
            if (seg === "OVERDUE") return r.overdueStatus && r.overdueStatus !== "NORMAL";
            return true;
          });
          if (bucket) rows = rows.filter((r) => clientMatchBucket(r, bucket));
          rows = [...rows].sort((a, b) => {
            const da = dueDay(a.paymentDate) ?? "9999-12-31";
            const db = dueDay(b.paymentDate) ?? "9999-12-31";
            if (da !== db) return da < db ? -1 : 1;
            return dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf();
          });
          const count = rows.length;
          const start = (page - 1) * pageSize;
          return { count, rows: rows.slice(start, start + pageSize) };
        }
        throw err;
      }
    },
    { revalidateOnFocus: false },
  );

  // Retry once after flipping aging param off
  useEffect(() => {
    if (!useAgingParam) mutate();
  }, [useAgingParam, mutate]);

  const rows = data?.rows ?? [];
  const total = data?.count ?? 0;

  // Oldest overdue for KPI
  useEffect(() => {
    if (!onOldestLoaded || !businessId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await NetworkSettlementApi.invoice({
          offset: { page: 1, limit: 1 },
          filter: { businessId, type: "OVERDUE" },
        });
        const row = res?.rows?.[0];
        if (cancelled) return;
        if (!row?.paymentDate) {
          onOldestLoaded(null);
          onFirstOverdueId?.(null);
          return;
        }
        const days = Math.abs(calendarDaysUntil(row.paymentDate));
        onOldestLoaded({
          refCode: row.refCode,
          days,
          amount: Number(row.amountToPay || 0),
        });
        onFirstOverdueId?.(row.id ?? null);
      } catch {
        if (!cancelled) {
          onOldestLoaded(null);
          onFirstOverdueId?.(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [businessId, onOldestLoaded, onFirstOverdueId]);

  // Earliest open date for statement default
  useEffect(() => {
    if (!onEarliestOpenDate || !businessId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await NetworkSettlementApi.invoice({
          offset: { page: 1, limit: 1 },
          filter: { businessId, type: "OPEN" },
        });
        const row = res?.rows?.[0];
        if (!cancelled) {
          onEarliestOpenDate(row?.paymentDate || row?.confirmedDate || null);
        }
      } catch {
        if (!cancelled) onEarliestOpenDate(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [businessId, onEarliestOpenDate]);

  const goDetails = useCallback(
    (inv: any) => {
      if (!inv?.id) return;
      const closed = inv.invoiceStatus === "CLOSED" || inv.paymentStatus === "CLOSED";
      navigate(closed ? `/closed-invoice/${inv.id}/details` : `/sales-invoice/${inv.id}/details`);
    },
    [navigate],
  );

  const bucketLabel = useMemo(() => {
    if (!bucket) return null;
    const map: Record<AgingBucketKey, string> = {
      NOT_DUE: t("billing:ar.agingCurrent"),
      D1_30: t("billing:ar.aging1to30"),
      D31_60: t("billing:ar.aging31to60"),
      D61_90: t("billing:ar.aging61to90"),
      D90_PLUS: t("billing:ar.aging91plus"),
    };
    return map[bucket];
  }, [bucket, t]);

  const formatSeg = (value: InvoiceSeg) => {
    const keys: Record<InvoiceSeg, string> = {
      OPEN: "billing:detail.segOpen",
      OVERDUE: "billing:detail.segOverdue",
      PENDING: "billing:detail.segPending",
      CLOSED: "billing:detail.segClosed",
    };
    const bare: Record<InvoiceSeg, string> = {
      OPEN: "billing:detail.segOpenBare",
      OVERDUE: "billing:detail.segOverdueBare",
      PENDING: "billing:detail.segPendingBare",
      CLOSED: "billing:detail.segClosedBare",
    };
    if (value === seg) return t(keys[value], { count: total });
    return t(bare[value]);
  };

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const showActions = seg === "OPEN" || seg === "OVERDUE";

  if (!INV_OPEN.hasAccess) {
    return <>{INV_OPEN.accessDenied()}</>;
  }

  return (
    <>
      <Box className={classes.filterRow}>
        <Box className={classes.segTabs} role="tablist">
          {(["OPEN", "OVERDUE", "PENDING", "CLOSED"] as InvoiceSeg[]).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={seg === value}
              className={`${classes.segTab} ${seg === value ? classes.segTabActive : ""}`}
              onClick={() => onSegChange(value)}
            >
              {formatSeg(value)}
            </button>
          ))}
        </Box>

        {bucket && (
          <button type="button" className={classes.bucketChip} onClick={onBucketClear}>
            {bucketLabel} ×
          </button>
        )}

        <Box className={classes.searchWrap}>
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.currentTarget.value)}
            placeholder={t("billing:detail.searchInvoice")}
            size="sm"
            styles={{ input: { ...filterInputStyles.input, width: "100%", height: 32 } }}
          />
        </Box>
      </Box>

      <Box className={classes.tblWrap}>
        {rows.length === 0 ? (
          <ListStates
            loading={isLoading}
            error={error}
            onRetry={() => mutate()}
            empty
            filtered={!!q}
            query={q}
            onClear={() => { setSearchInput(""); onSearchChange(""); }}
            columns={showActions ? 8 : 7}
          />
        ) : (
        <Box component="table" className={classes.tbl}>
          <Box component="thead">
            <Box component="tr" className={listSticky.scrollRow}>
              {showActions && (
                <Box
                  component="th"
                  className={`${classes.colActions} ${listSticky.stickyActions} ${listSticky.stickyEdge}`}
                  style={{ width: 52 }}
                >
                  {t("billing:col.actions")}
                </Box>
              )}
              <Box component="th" className={classes.colNum}>
                {t("billing:invoiceCol.number")}
              </Box>
              <Box component="th" className={classes.colDate}>
                {t("billing:invoiceCol.confirmed")}
              </Box>
              <Box component="th" className={classes.colDate}>
                {t("billing:invoiceCol.payDate")}
              </Box>
              <Box component="th" className={classes.colDue}>
                {t("billing:invoiceCol.due")}
              </Box>
              <Box component="th" className={classes.colAmt}>
                {t("billing:invoiceCol.amount")}
              </Box>
              <Box component="th" className={classes.colPaid}>
                {t("billing:invoiceCol.paid")}
              </Box>
              <Box component="th" className={classes.colRemain}>
                {t("billing:invoiceCol.remaining")}
              </Box>
            </Box>
          </Box>
          <Box component="tbody">
            {rows.map((inv: any, i: number) => {
                    const totalAmt = Number(inv.confirmedAmount || 0);
                    const paidAmt = Number(inv.paidAmount || 0);
                    const remainingAmt = Number(
                      inv.amountToPay ?? Math.max(0, totalAmt - paidAmt),
                    );
                    return (
                      <Box
                        component="tr"
                        key={inv.id ?? i}
                        className={listSticky.scrollRow}
                        onClick={() => goDetails(inv)}
                      >
                        {showActions && (
                          <Box
                            component="td"
                            className={`${classes.colActions} ${listSticky.stickyActions} ${listSticky.stickyEdge}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <RowActions
                              items={[
                                {
                                  key: "extend",
                                  intent: "edit",
                                  icon: <CalendarPlusIcon size={17} />,
                                  label: t("billing:tooltip.extend"),
                                  onClick: () => {
                                    setSelectedInvoiceId(inv.id);
                                    setSelectedInvoiceNumber(inv.refCode);
                                    setSelectedDueDate(inv.paymentDate);
                                    extendHandlers.open();
                                  },
                                  hidden: !(inv.amountToPay > 0),
                                },
                                {
                                  key: "lateFee",
                                  intent: "default",
                                  icon: <CurrencyCircleDollarIcon size={17} />,
                                  label: t("billing:tooltip.lateFee"),
                                  onClick: () => {
                                    setSelectedInvoiceId(inv.id);
                                    lateFeeHandlers.open();
                                  },
                                  hidden:
                                    !lateFeeEnabled ||
                                    !(inv.overdueStatus && inv.overdueStatus !== "NORMAL"),
                                },
                                {
                                  key: "reminder",
                                  intent: "default",
                                  icon: <BellIcon size={17} />,
                                  label: t("billing:tooltip.reminder"),
                                  onClick: () => {
                                    setSelectedInvoiceId(inv.id);
                                    reminderHandlers.open();
                                  },
                                },
                              ]}
                            />
                          </Box>
                        )}
                        <Box component="td" className={classes.colNum}>
                          <DocNoCell
                            value={inv.refCode}
                            showHash={false}
                            color="dark"
                            fz={14}
                            fw={500}
                            onClick={() => goDetails(inv)}
                          />
                        </Box>
                        <Box component="td" className={`${classes.colDate} ${classes.colMuted}`}>
                          {inv.confirmedDate || inv.sentDate
                            ? dateFormat(inv.confirmedDate || inv.sentDate)
                            : "-"}
                        </Box>
                        <Box component="td" className={`${classes.colDate} ${classes.colMuted}`}>
                          {dueDay(inv.paymentDate) ?? "-"}
                        </Box>
                        <Box component="td" className={classes.colDue}>
                          {seg === "CLOSED" ? (
                            <Text className={classes.amtMuted}>-</Text>
                          ) : (
                            <DuePill paymentDate={inv.paymentDate} />
                          )}
                        </Box>
                        <Box component="td" className={`${classes.colAmt} ${classes.colMuted}`}>
                          {currencyFormat(totalAmt, { withSymbol: false })}
                        </Box>
                        <Box
                          component="td"
                          className={`${classes.colPaid} ${paidAmt > 0 ? classes.amtPaid : classes.amtMuted}`}
                        >
                          {paidAmt > 0 ? currencyFormat(paidAmt, { withSymbol: false }) : "-"}
                        </Box>
                        <Box component="td" className={classes.colRemain}>
                          {remainingAmt > 0
                            ? currencyFormat(remainingAmt, { withSymbol: false })
                            : "-"}
                        </Box>
                      </Box>
                    );
                  })}
          </Box>
        </Box>
        )}
      </Box>

      <ListFooter
        from={from}
        to={to}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />

      <ExtendPaymentModal
        invoiceId={selectedInvoiceId}
        invoiceNumber={selectedInvoiceNumber}
        currentDueDate={selectedDueDate}
        opened={extendOpened}
        onClose={extendHandlers.close}
        onSuccess={() => mutate()}
      />
      <LateFeeDrawer
        invoiceId={selectedInvoiceId}
        opened={lateFeeOpened}
        onClose={lateFeeHandlers.close}
        onOpenReminder={() => {
          lateFeeHandlers.close();
          reminderHandlers.open();
        }}
      />
      <ReminderDrawer
        invoiceId={selectedInvoiceId}
        opened={reminderOpened}
        onClose={reminderHandlers.close}
      />
    </>
  );
}
